import type { CleanupTransactionActionBatch } from './features/cleanup-transaction';
import type { RecategorizeTransactionActionBatch } from './features/recategorize';
import type { RenamePayeeTransactionActionBatch } from './features/rename-payee';
import type { TransactionActionExecutionResult } from './proposalTypes';

// Type guards for narrowing the shared TransactionActionExecutionResult envelope down to a
// specific capability's 'proposals' variant, for UI code that only cares about one capability
// (e.g. a review drawer for rename_payee shouldn't have to deal with cleanup_transaction's shape).

// TypeScript narrows `result.batch` correctly on a nested `result.batch.capability === 'x'`
// check, but does not propagate that narrowing to sibling fields (like `proposals`) within
// the same union member - nested discriminants don't auto-narrow the rest of the object the
// way a top-level `status` check does. This explicit type guard is the idiomatic fix.
export type RecategorizeProposalsResult = Extract<
  TransactionActionExecutionResult,
  { status: 'proposals'; batch: RecategorizeTransactionActionBatch }
>;

export const isRecategorizeProposalsResult = (
  result: TransactionActionExecutionResult,
): result is RecategorizeProposalsResult => result.status === 'proposals' && result.batch.capability === 'recategorize';

export type RenamePayeeProposalsResult = Extract<
  TransactionActionExecutionResult,
  { status: 'proposals'; batch: RenamePayeeTransactionActionBatch }
>;

export const isRenamePayeeProposalsResult = (
  result: TransactionActionExecutionResult,
): result is RenamePayeeProposalsResult => result.status === 'proposals' && result.batch.capability === 'rename_payee';

export type CleanupTransactionProposalsResult = Extract<
  TransactionActionExecutionResult,
  { status: 'proposals'; batch: CleanupTransactionActionBatch }
>;

export const isCleanupTransactionProposalsResult = (
  result: TransactionActionExecutionResult,
): result is CleanupTransactionProposalsResult =>
  result.status === 'proposals' && result.batch.capability === 'cleanup_transaction';
